import React, { useState } from 'react';
import {
  Container, Typography, Box, TextField, Button, Card, CardContent,
  Stepper, Step, StepLabel, Chip, Grid, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const repairSteps = ['Received', 'Diagnosing', 'In Repair', 'Quality Check', 'Ready for Pickup'];

function getTicketFromStorage(ticketId) {
  try {
    const requests = JSON.parse(localStorage.getItem('serviceRequests') || '[]');
    return requests.find(
      (r) =>
        r.id === ticketId ||
        r.id === `#${ticketId}` ||
        String(r.id) === String(ticketId) ||
        String(r.osTicketNumber) === String(ticketId)
    ) || null;
  } catch {
    return null;
  }
}

function normalizeOsTicketStatus(status) {
  if (!status) return 'Pending';
  const s = status.toLowerCase();
  if (s.includes('open'))     return 'In Progress';
  if (s.includes('closed') || s.includes('resolved')) return 'Completed';
  if (s.includes('pending'))  return 'Pending';
  return status;
}

function statusToStep(status) {
  if (!status) return 0;
  const s = status.toLowerCase();
  if (s === 'pending')     return 0;
  if (s === 'in progress') return 2;
  if (s === 'completed')   return 4;
  if (s === 'cancelled')   return -1;
  return 0;
}

function RepairTracker() {
  const [ticketId, setTicketId] = useState('');
  const [result, setResult]     = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleSearch = async () => {
    const id = ticketId.trim();
    if (!id) return;
    setLoading(true);
    setSearched(false);
    setResult(null);

    try {
      // ── 1. Try live osTicket lookup first ────────────────────────────
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/ticket-status.php?ticket=${encodeURIComponent(id)}`
        : `http://localhost:5000/api/ticket-status/${encodeURIComponent(id)}`;
      const res = await fetch(apiUrl);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.ticket) {
          const normalized = normalizeOsTicketStatus(data.ticket.status);
          setResult({
            id: data.ticket.ticketNumber || id,
            customerName: data.ticket.name || '',
            service: data.ticket.subject || '',
            status: normalized,
            date: data.ticket.created ? data.ticket.created.split(' ')[0] : '',
            source: 'osticket',
          });
          setSearched(true);
          setLoading(false);
          return;
        }
      }
    } catch {
      // Server not running — fall through to localStorage
    }

    // ── 2. Fall back to localStorage ──────────────────────────────────
    const local = getTicketFromStorage(id);
    setResult(local);
    setSearched(true);
    setLoading(false);
  };

  const activeStep = result ? statusToStep(result.status) : 0;
  const isCancelled = result?.status?.toLowerCase() === 'cancelled';

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: '10%', right: '5%',
        width: 360, height: 360,
        background: 'radial-gradient(circle, rgba(255,107,107,0.09) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, py: 10 }}>
        <Container maxWidth="sm">

          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, display: 'block', mb: 2 }}>
              Real-Time Updates
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
              Track Your{' '}
              <Box component="span" className="gradient-text">Repair</Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Enter your ticket ID to check the current status of your repair.
            </Typography>
          </Box>

          {/* Search box */}
          <Card sx={{
            background: 'rgba(255,107,107,0.05)',
            border: '1px solid rgba(255,107,107,0.15)',
            borderRadius: '18px',
            backdropFilter: 'blur(10px)',
            mb: 4,
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your ticket ID was sent to your email when you booked a service.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder="e.g. 1234 or SR-1234"
                  value={ticketId}
                  onChange={(e) => setTicketId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      background: 'rgba(255,255,255,0.04)',
                      '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#ff6b6b' },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                    color: '#2d3436', fontWeight: 700, px: 3, flexShrink: 0,
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                    '&.Mui-disabled': { opacity: 0.6 },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? <CircularProgress size={20} sx={{ color: '#2d3436' }} /> : <SearchIcon />}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Result */}
          {searched && !result && (
            <Card sx={{
              background: 'rgba(255,107,107,0.05)',
              border: '1px solid rgba(255,107,107,0.2)',
              borderRadius: '16px', p: 3, textAlign: 'center',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>Ticket not found</Typography>
              <Typography variant="body2" color="text.secondary">
                Double-check your ticket ID or contact us at +1 (617) 751-8481.
              </Typography>
            </Card>
          )}

          {result && (
            <Card sx={{
              background: 'rgba(255,107,107,0.05)',
              border: '1px solid rgba(255,107,107,0.2)',
              borderRadius: '18px',
              backdropFilter: 'blur(10px)',
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600 }}>Ticket #{result.id}</Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                      {result.service || 'Repair Service'}
                    </Typography>
                  </Box>
                  <Chip
                    label={result.status || 'Pending'}
                    sx={{
                      fontWeight: 700,
                      background: isCancelled
                        ? 'rgba(255,70,70,0.15)'
                        : result.status === 'Completed'
                          ? 'rgba(0,200,100,0.15)'
                          : 'rgba(255,107,107,0.15)',
                      color: isCancelled ? '#ff4646' : result.status === 'Completed' ? '#00c864' : 'primary.main',
                      border: '1px solid',
                      borderColor: isCancelled ? 'rgba(255,70,70,0.3)' : 'rgba(255,107,107,0.3)',
                    }}
                  />
                </Box>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {[
                    { icon: AccessTimeIcon, label: 'Date', value: result.preferredDate || result.date || '—' },
                    { icon: BuildIcon,      label: 'Device', value: result.deviceBrand || result.deviceType || '—' },
                  ].map((item, i) => (
                    <Grid item xs={6} key={i}>
                      <Box sx={{
                        p: 2, borderRadius: '12px',
                        background: 'rgba(255,107,107,0.06)',
                        border: '1px solid rgba(255,107,107,0.1)',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {React.createElement(item.icon, { sx: { fontSize: 16, color: 'primary.main' } })}
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                        </Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{item.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {!isCancelled && (
                  <Stepper activeStep={activeStep} orientation="vertical" sx={{
                    '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.5)' },
                    '& .MuiStepLabel-label.Mui-active': { color: '#fff', fontWeight: 700 },
                    '& .MuiStepLabel-label.Mui-completed': { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiStepIcon-root': { color: 'rgba(255,107,107,0.2)' },
                    '& .MuiStepIcon-root.Mui-active': { color: '#ff6b6b' },
                    '& .MuiStepIcon-root.Mui-completed': { color: '#00c864' },
                    '& .MuiStepConnector-line': { borderColor: 'rgba(255,107,107,0.15)' },
                  }}>
                    {repairSteps.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                )}

                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255,107,107,0.1)', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Questions? Call us at{' '}
                    <Box component="a" href="tel:+16177518481" sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}>
                      +1 (617) 751-8481
                    </Box>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

        </Container>
      </Box>
    </Box>
  );
}

export default RepairTracker;
